import React, { useEffect, useRef, useContext, useMemo } from "react";
import { Form, Row, Col, Badge } from "react-bootstrap";
import Select from "react-select";
import WeekDaysSelector from "../../components/WeekDaysSelector";
import StarekContext from "../../context/StarekContext";
import {
    SidebarView,
    SidebarContext,
    SidebarContextProvider,
    useSyncedField,
} from "../../components/Sidebar";

function ShiftBasicDetails() {
    const { entity } = useContext(SidebarContext);
    const [shiftName, setShiftName] = useSyncedField("name", "");
    const [shiftDesc, setShiftDesc] = useSyncedField("description", "");
    const [weekDays, setWeekDays] = useSyncedField("weekDays", 62); // Default: Mon-Fri
    const [startSec, setStartSec] = useSyncedField("startSec", 8 * 3600); // 8 AM
    const [durSec, setDurSec] = useSyncedField("durSec", 9 * 3600); // 9 hours

    const entityIdRef = useRef(null);

    useEffect(() => {
        const currentEntityId = entity?.id || null;
        if (
            entityIdRef.current === currentEntityId &&
            currentEntityId !== null
        ) {
            return;
        }
        entityIdRef.current = currentEntityId;

        if (entity) {
            setShiftName(entity.name || "");
            setShiftDesc(entity.description || "");
            setWeekDays(entity.runs_on ?? 62);

            const s = Number(entity.start_time) || 0;
            const e = Number(entity.end_time) || 0;
            setStartSec(s);
            const d = (e - s + 86400) % 86400; // Handle overnight shifts
            setDurSec(d);
        } else {
            setShiftName("");
            setShiftDesc("");
            setWeekDays(62);
            setStartSec(8 * 3600);
            setDurSec(9 * 3600);
        }
    }, [entity?.id]);

    const fmt12h = (s) => {
        const hh = Math.floor(s / 3600);
        const mm = Math.floor((s % 3600) / 60);
        const h12 = ((hh + 11) % 12) + 1;
        const ampm = hh < 12 ? "AM" : "PM";
        return `${h12}:${mm.toString().padStart(2, "0")} ${ampm}`;
    };

    const toSec = (h, m) => h * 3600 + m * 60;

    const endSec = useMemo(
        () => (startSec + durSec) % 86400,
        [startSec, durSec],
    );

    const crossesMidnight = useMemo(
        () => startSec + durSec >= 86400,
        [startSec, durSec],
    );

    const startTimeOptions = useMemo(() => {
        const opts = [];
        for (let h = 0; h < 24; h++) {
            for (let m of [0, 30]) {
                const s = toSec(h, m);
                opts.push({ value: s, label: fmt12h(s) });
            }
        }
        return opts;
    }, []);

    const durationOptions = useMemo(() => {
        const opts = [];
        for (let m = 15; m <= 24 * 60; m += 15) {
            const h = Math.floor(m / 60);
            const mm = m % 60;
            const label =
                h > 0 && mm > 0
                    ? `${h} hr ${mm} min`
                    : h > 0
                      ? `${h} hr${h === 1 ? "" : "s"}`
                      : `${mm} min`;
            opts.push({ value: m * 60, label });
        }
        return opts;
    }, []);

    return (
        <Form
            className="d-flex flex-column h-100"
            onSubmit={(e) => e.preventDefault()}
        >
            <Form.Group className="mb-3">
                <Form.Label>
                    Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Enter here"
                    value={shiftName}
                    onChange={(e) => setShiftName(e.target.value)}
                    required
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Max allowed characters: 120"
                    value={shiftDesc}
                    onChange={(e) => setShiftDesc(e.target.value)}
                />
            </Form.Group>

            <div className="border p-2 mt-2">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>Timings</div>
                    <div className="d-flex align-items-center">
                        <WeekDaysSelector
                            initialValue={weekDays}
                            onChange={(days) => setWeekDays(days)}
                        />
                    </div>
                </div>

                <Row className="g-2">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Start time</Form.Label>
                            <Select
                                inputId="start_time_select"
                                options={startTimeOptions}
                                value={startTimeOptions.find(
                                    (o) => o.value === startSec,
                                )}
                                onChange={(o) => o && setStartSec(o.value)}
                                isClearable={false}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Duration</Form.Label>
                            <Select
                                inputId="duration_select"
                                options={durationOptions}
                                value={
                                    durationOptions.find(
                                        (o) => o.value === durSec,
                                    ) || null
                                }
                                onChange={(o) => o && setDurSec(o.value)}
                                isClearable={false}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <div className="mt-2 small">
                    <strong>Preview:</strong> {fmt12h(startSec)} →{" "}
                    {fmt12h(endSec)}{" "}
                    {crossesMidnight && <Badge bg="secondary">next day</Badge>}
                </div>
            </div>
        </Form>
    );
}

const ShiftSidebar = (props) => {
    const {
        show,
        onDiscard,
        item = null,
        plantId,
        onSave = async () => {},
        onDelete = async () => {},
    } = props;
    const starekContext = useContext(StarekContext);

    const handleSaveInternal = async (emittedData, entity) => {
        try {
            const shiftName = emittedData.name?.trim();
            if (!shiftName) {
                starekContext.showError("Shift name is required");
                throw new Error("Shift name is required");
            }
            if (!plantId) {
                starekContext.showError("Plant ID is required");
                throw new Error("Plant ID is required");
            }

            const startSec = emittedData.startSec || 0;
            const durSec = emittedData.durSec || 0;
            const endSec = (startSec + durSec) % 86400;

            const payload = {
                id: entity?.id,
                name: shiftName,
                description: emittedData.description || "",
                start_time: startSec,
                end_time: endSec,
                runs_on: emittedData.weekDays || 62,
            };

            await onSave(payload);

            starekContext.showSuccess(
                entity?.id
                    ? `Shift "${shiftName}" updated successfully`
                    : `Shift "${shiftName}" created successfully`,
            );
        } catch (error) {
            console.error("Error saving shift:", error);
            starekContext.showError(
                error?.message || "Failed to save shift. Please try again.",
            );
            throw error;
        }
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={onDelete}
            onDiscard={onDiscard}
        >
            <SidebarView entityName="Shift">
                <SidebarView.Page
                    key={item?.id || "new"}
                    name="basic_details"
                    title="Basic Details"
                >
                    <ShiftBasicDetails />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
};

export default ShiftSidebar;
